import SEO from "@/components/feature/SEO";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { videos } from "@/mocks/videos";
import { articles } from "@/mocks/articles";
import type { Video } from "@/mocks/types";
import type { Article } from "@/mocks/types";

const categoryNames: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
  anime: "动漫",
  documentary: "纪录片",
  music: "音乐",
  sports: "体育",
  game: "游戏",
};

function RelatedCard({ item }: { item: Video | Article }) {
  return (
    <Link
      to={`/article/${item.id}`}
      className="group flex gap-3 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/60 p-3 transition-all duration-300"
    >
      <div className="relative w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={item.thumbnail}
          alt={item.title}
          title={item.title}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
          {item.title}
        </h4>
        <p className="text-xs text-foreground-400 mt-1">{(item.views / 10000).toFixed(0)}万次播放</p>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const allItems = [...videos, ...articles];
  const item = allItems.find((i) => i.id === id);

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <p className="text-foreground-500 text-lg mb-4">未找到相关内容</p>
            <Link to="/" className="inline-flex items-center gap-2 text-primary-500 font-medium">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catName = categoryNames[item.category] || item.category;
  const relatedVideos = videos.filter((v) => v.category === item.category && v.id !== item.id).slice(0, 4);
  const excerptText = "excerpt" in item ? item.excerpt : item.description;

  return (
    <>
      <SEO
        title={`${item.title} - 视频详情 | 红桃视频网页版`}
        description={excerptText.substring(0, 160)}
        keywords={`红桃视频网页版,${item.title},${item.tags.join(",")}`}
        canonical={`https://www.hongtao-video.com/article/${id}`}
      />

      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: excerptText,
            datePublished: item.publishDate,
            author: { "@type": "Person", name: "excerpt" in item ? (item as Article).author : "红桃视频" },
            publisher: { "@type": "Organization", name: "红桃视频网页版" },
          }),
        }}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-100">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: catName, path: `/category/${item.category}` },
                { label: item.title },
              ]}
            />
          </div>

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs bg-primary-100 text-primary-600 px-3 py-1 rounded-full font-medium">
                    {catName}
                  </span>
                  <span className="text-xs text-foreground-400">{item.publishDate}</span>
                  <span className="text-xs text-foreground-400">{(item.views / 10000).toFixed(1)}万次播放</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  {item.title}
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  {item.tags.map((tag: string) => (
                    <span key={tag} className="text-xs text-secondary-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </header>

              <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-8 bg-black">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  title={`${item.title} - 红桃视频在线观看`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500/90 rounded-full cursor-pointer hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-white text-4xl md:text-5xl ml-1"></i>
                  </div>
                </div>
              </div>

              <div className="prose prose-sm max-w-none mb-12">
                {"content" in item
                  ? (item.content as string).split("\n").map((para: string, idx: number) =>
                      para.trim() ? (
                        <p key={idx} className="text-sm text-foreground-700 leading-relaxed mb-4">
                          {para}
                        </p>
                      ) : null
                    )
                  : (
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      {item.description}
                    </p>
                  )
                }
              </div>

              {relatedVideos.length > 0 && (
                <section className="border-t border-background-200 pt-8">
                  <h3 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedVideos.map((v) => (
                      <RelatedCard key={v.id} item={v} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}